import React, { useEffect, useId, useRef, useState } from 'react';
import { identityAPI } from '../../services/api';
import { apiErrorMessage } from './propertyRegistry';

export default function PropertyPartyPicker({
  tenantSlug,
  value,
  onChange,
  label = 'व्यक्ती किंवा संस्था शोधा',
  disabled = false,
  required = false,
}) {
  const id = useId();
  const requestId = useRef(0);
  const [query, setQuery] = useState('');
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (value || query.trim().length < 2 || disabled) {
      setItems([]);
      setOpen(false);
      return undefined;
    }
    const currentRequest = ++requestId.current;
    const timer = window.setTimeout(async () => {
      setLoading(true);
      setError('');
      try {
        const response = await identityAPI.searchParties(tenantSlug, {
          q: query.trim(),
          page: 1,
          limit: 12,
          profile_status: 'active',
          party_status: 'active',
        });
        if (currentRequest !== requestId.current) return;
        setItems(response.data.parties || []);
        setOpen(true);
      } catch (requestError) {
        if (currentRequest !== requestId.current) return;
        setItems([]);
        setOpen(true);
      setError(apiErrorMessage(requestError, 'व्यक्ती किंवा संस्था शोध उपलब्ध नाही.'));
      } finally {
        if (currentRequest === requestId.current) setLoading(false);
      }
    }, 300);
    return () => window.clearTimeout(timer);
  }, [disabled, query, tenantSlug, value]);

  const choose = party => {
    onChange(party);
    setQuery('');
    setItems([]);
    setOpen(false);
  };

  return (
    <div className="property-party-picker">
      <label htmlFor={`${id}-input`}>{label}{required ? ' *' : ''}</label>
      {value ? (
        <div className="property-party-selected">
          <span>
            <strong>{value.display_name || value.legal_name}</strong>
            <small>{value.party_type} · {value.primary_phone || value.primary_email || value.party_uid}</small>
          </span>
          {!disabled && (
            <button type="button" className="property-icon-button" onClick={() => onChange(null)} aria-label={`Remove ${value.legal_name}`}>
              ×
            </button>
          )}
        </div>
      ) : (
        <div className="property-combobox-wrap">
          <input
            id={`${id}-input`}
            type="search"
            value={query}
            disabled={disabled}
            required={required}
            role="combobox"
            aria-autocomplete="list"
            aria-expanded={open}
            aria-controls={`${id}-results`}
            aria-describedby={`${id}-hint`}
            placeholder="नाव, मोबाईल, ईमेल किंवा Party UID"
            onChange={event => setQuery(event.target.value)}
            onFocus={() => items.length && setOpen(true)}
            autoComplete="off"
          />
          <small id={`${id}-hint`} className="property-field-hint">
        किमान २ अक्षरे टाइप करा
          </small>
      {loading && <span className="property-inline-loader" role="status">शोधत आहे…</span>}
          {open && (
            <div id={`${id}-results`} className="property-combobox-results" role="listbox">
              {error && <p className="property-combobox-message" role="alert">{error}</p>}
              {!error && !loading && items.length === 0 && (
          <p className="property-combobox-message">जुळणारी व्यक्ती किंवा संस्था सापडली नाही.</p>
              )}
              {items.map(party => (
                <button
                  type="button"
                  role="option"
                  aria-selected="false"
                  key={party.party_uid}
                  onMouseDown={event => event.preventDefault()}
                  onClick={() => choose(party)}
                >
                  <strong>{party.display_name || party.legal_name}</strong>
              <span>{party.party_type === 'individual' ? 'व्यक्ती' : 'संस्था'} · {party.primary_phone || party.primary_email || 'संपर्क उपलब्ध नाही'}</span>
              <small>{party.tenant_profile?.profile_kind === 'resident' ? 'ग्रामस्थ' : party.tenant_profile?.profile_kind === 'external_taxpayer' ? 'बाह्य करदाता' : 'प्रोफाइल'} · {party.party_uid}</small>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
